/**
 * Sync queue processor with exponential backoff
 * @module sync
 */

import { 
  getPendingSyncItems, 
  updateSyncStatus, 
  markSynced, 
  removeSyncItem, 
  getPendingCount,
  recoverOrphanedItems 
} from './db.js';
import { api, ApiError } from './api.js';
import { appState } from './state.js';
import { sleep } from './utils.js';

/** Maximum items per batch (Google Sheets API efficiency) */
const MAX_BATCH_SIZE = 10;

/** Maximum retries before permanent failure */
const MAX_RETRIES = 5;

/** Base delay for exponential backoff (ms) */
const BASE_DELAY = 1000;

/** Maximum delay cap (ms) */
const MAX_DELAY = 64000;

/** Sync in progress flag */
let syncInProgress = false;

/** Currently syncing item (for recovery tracking) */
let currentSyncingItem = null;

/**
 * Calculate backoff delay with jitter
 * @param {number} retryCount - Current retry attempt
 * @returns {number} Delay in milliseconds
 */
function calculateBackoff(retryCount) {
  const exponentialDelay = Math.min(Math.pow(2, retryCount) * BASE_DELAY, MAX_DELAY);
  const jitter = Math.random() * 1000;
  return exponentialDelay + jitter;
}

/**
 * Process the sync queue with batching and exponential backoff
 * @param {import('idb').IDBPDatabase} db - Database instance
 * @returns {Promise<void>}
 */
export async function processSyncQueue(db) {
  // Guard: must be online
  if (!navigator.onLine) {
    console.log('[Sync] Offline — skipping');
    return;
  }
  
  // Guard: prevent concurrent execution
  if (syncInProgress) {
    console.log('[Sync] Already in progress — skipping');
    return;
  }

  syncInProgress = true;
  appState.set('syncStatus', 'syncing');

  try {
    // Recover orphaned items first
    const recovered = await recoverOrphanedItems(db);
    if (recovered > 0) {
      console.log(`[Sync] Recovered ${recovered} orphaned items`);
    }

    let hasMore = true;
    
    while (hasMore && navigator.onLine) {
      const pending = await getPendingSyncItems(db);
      
      if (pending.length === 0) {
        hasMore = false;
        continue;
      }
      
      const batch = pending.slice(0, MAX_BATCH_SIZE);
      console.log(`[Sync] Processing batch of ${batch.length} items`);
      
      for (const item of batch) {
        // Check connectivity before each item
        if (!navigator.onLine) {
          console.log('[Sync] Connection lost — pausing');
          break;
        }
        
        await updateSyncStatus(db, item.id, 'processing');
        currentSyncingItem = item; // Track for crash protection
        
        try {
          // API expects { expense: { ... } } wrapper
          const result = await api.post('/api/expense', { expense: item.data });
          
          if (result.success) {
            await markSynced(db, item.clientId, result.serverId || null);
            await removeSyncItem(db, item.id);
            console.log(`[Sync] Item ${item.clientId} synced successfully`);
          } else {
            throw new ApiError(400, result.error || 'Unknown error');
          }
        } catch (err) {
          console.error(`[Sync] Item ${item.clientId} failed:`, err.message);
          
          // ALWAYS reset to pending or failed - NEVER leave in processing
          if (err.isAuthError) {
            await updateSyncStatus(db, item.id, 'pending', item.retryCount, 'Authentication required');
            appState.set('syncStatus', 'auth-error');
            currentSyncingItem = null;
            syncInProgress = false;
            return;
          } else if (err.isRetryable && item.retryCount < MAX_RETRIES) {
            const delay = calculateBackoff(item.retryCount);
            await updateSyncStatus(db, item.id, 'pending', item.retryCount + 1, err.message);
            console.log(`[Sync] Will retry in ${Math.round(delay)}ms (attempt ${item.retryCount + 1})`);
            await sleep(delay);
          } else {
            // Permanent failure — keep in queue as 'failed' so user can see it
            await updateSyncStatus(db, item.id, 'failed', item.retryCount, err.message);
            console.error(`[Sync] Item ${item.clientId} permanently failed - kept in queue`);
          }
        } finally {
          currentSyncingItem = null;
        }
      }
      
      // Check if more items remain
      const remaining = await getPendingCount(db);
      hasMore = remaining > 0;
    }
    
    appState.set('syncStatus', 'idle');
    appState.set('lastSyncTime', Date.now());
    
  } catch (err) {
    console.error('[Sync] Queue processing error:', err);
    appState.set('syncStatus', 'error');
  } finally {
    syncInProgress = false;
    // Update pending count
    try {
      const count = await getPendingCount(db);
      appState.set('pendingCount', count);
    } catch (e) {
      console.error('[Sync] Failed to get pending count:', e);
    }
  }
}

/**
 * Setup sync triggers for iOS Safari fallback (no Background Sync API)
 * Triggers: online, visibilitychange, focus
 * @param {import('idb').IDBPDatabase} db - Database instance
 */
export function setupSyncTriggers(db) {
  // Online event
  window.addEventListener('online', () => {
    console.log('[Sync] Online — triggering sync');
    processSyncQueue(db);
  });

  // Visibility change (iOS Safari primary sync trigger)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      console.log('[Sync] Visible + online — triggering sync');
      processSyncQueue(db);
    }
  });

  // Focus event (additional iOS trigger)
  window.addEventListener('focus', () => {
    if (navigator.onLine) {
      console.log('[Sync] Focused + online — triggering sync');
      processSyncQueue(db);
    }
  });

  // Initial sync on setup
  if (navigator.onLine) {
    console.log('[Sync] Initial sync');
    processSyncQueue(db);
  }
}

/**
 * Check if sync is currently in progress
 * @returns {boolean}
 */
export function isSyncing() {
  return syncInProgress;
}
